import React from 'react';
import { formatDate, formatShortDate } from '../../../utils/competitionRequestUtils';

const QuickInfoSidebar = ({ 
  request, 
  isSentRequest,
  onAcceptRequest, 
  onRejectRequest, 
  onCancelRequest, 
  actionLoading 
}) => {
  return (
    <div className="space-y-6">
      {/* Quick Info */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-[#E96A37] to-orange-500 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900">Quick Information</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-gradient-to-r from-[#E96A37]/5 to-orange-50 rounded-lg">
            <span className="text-sm text-gray-600 font-medium">Match Date:</span>
            <span className="text-sm font-bold text-[#E96A37]">
              {request.proposedMatchDate ? formatShortDate(request.proposedMatchDate) : 'Not specified'}
            </span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600 font-medium">Start Date:</span>
            <span className="text-sm font-bold text-gray-900">
              {request.proposedStartDate ? formatShortDate(request.proposedStartDate) : 'Not specified'}
            </span>
          </div>
          
          {request.proposedEndDate && (
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 font-medium">End Date:</span>
              <span className="text-sm font-bold text-gray-900">
                {formatShortDate(request.proposedEndDate)}
              </span>
            </div>
          )}
          
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600 font-medium">Created:</span>
            <span className="text-sm font-bold text-gray-900">
              {request.createdAt ? formatShortDate(request.createdAt) : 'Not specified'}
            </span>
          </div>
          
          {request.processedAt && (
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600 font-medium">Processed:</span>
              <span className="text-sm font-bold text-gray-900">
                {formatShortDate(request.processedAt)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
            <span className="text-sm text-gray-600 font-medium">Request Age:</span>
            <span className="text-sm font-bold text-blue-700">
              {request.ageInDays !== undefined ? `${request.ageInDays} day${request.ageInDays !== 1 ? 's' : ''}` : 'Unknown'}
            </span>
          </div>

          {request.daysUntilMatch > 0 && (
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
              <span className="text-sm text-gray-600 font-medium">Days Until Match:</span>
              <span className="text-sm font-bold text-green-700">
                {request.daysUntilMatch} day{request.daysUntilMatch !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {request.status === 'pending' && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-[#E96A37] to-orange-500 rounded-full flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
            </div>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Actions</h2>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                isSentRequest 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {isSentRequest ? 'Sender' : 'Receiver'}
              </span>
            </div>
                      </div>
            
            {/* Role Description */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                {isSentRequest 
                  ? 'You sent this competition request. You can cancel it at any time while it\'s pending.'
                  : 'You received this competition request. You can accept or reject it.'
                }
              </p>
            </div>
            
            <div className="space-y-3">
            {/* Receiver Actions: Accept and Reject */}
            {!isSentRequest && (
              <>
                <button
                  onClick={onAcceptRequest}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-bold">{actionLoading ? 'Accepting...' : 'Accept Request'}</span>
                </button>
                
                <button
                  onClick={onRejectRequest}
                  disabled={actionLoading}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-bold">Reject Request</span>
                </button>
              </>
            )}
            
            {/* Sender Actions: Cancel only */}
            {isSentRequest && (
              <button
                onClick={onCancelRequest}
                disabled={actionLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-gray-700 bg-white border-2 border-gray-300 hover:border-[#E96A37] hover:text-[#E96A37] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E96A37] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span className="font-bold">{actionLoading ? 'Cancelling...' : 'Cancel Request'}</span>
              </button>
            )}
            
            {/* No actions if no role match or request not actionable */}
            {!isSentRequest && request.status === 'pending' && (!request.canAccept && !request.canReject) && (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">No actions available for this request</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Event Details (if approved) */}
      {request.eventId && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900">Created Event</h2>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-green-800">{request.eventId.name}</h3>
              <span className="text-sm text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">
                {request.eventId.eventStatus}
              </span>
            </div>
            
            <div className="space-y-2 text-sm text-green-700">
              <div className="flex justify-between">
                <span className="font-medium">Start Date:</span>
                <span>{formatDate(request.eventId.startDate)}</span>
              </div>
              {request.eventId.endDate && (
                <div className="flex justify-between">
                  <span className="font-medium">End Date:</span>
                  <span>{formatDate(request.eventId.endDate)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickInfoSidebar; 